import { NgClass, NgIf } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { catchError, finalize, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { IMessage } from 'src/app/shared/interface/common';
import { ChatReportService } from 'src/app/shared/services/chat-report.service';
import { ChatWindowService } from 'src/app/shared/services/chat-window.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { AppHelper } from 'src/app/shared/utilities/app.helper';
import { TextInputValidate } from '../../../shared/directives/text-input-validate.directive';
import { UtileFormatFileSizePipe } from '../../../shared/pipes/utile/utile-formatFileSizePipe.pipe';

@Component({
  selector: 'app-chat-report',
  templateUrl: './chat-report.component.html',
  styleUrls: ['./chat-report.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextInputValidate,
    NgClass,
    NgIf,
    TextareaModule,
    FileUploadModule,
    PrimeTemplate,
    TableModule,
    SelectModule,
    ButtonModule,
    UtileFormatFileSizePipe,
  ],
})
export class ChatReportComponent implements OnInit {
  @ViewChild('supportCaseAttachments') supportCaseAttachments!: FileUpload;
  priorities = [
    { priorityId: 0, priorityName: 'Low' },
    { priorityId: 1, priorityName: 'Medium' },
    { priorityId: 2, priorityName: 'High' },
  ];
  uploadedFiles: File[] = [];
  formGroupReportIssue: FormGroup = this.fb.group({
    subject: ['', [Validators.required, Validators.maxLength(50)]],
    description: ['', [Validators.required, Validators.maxLength(2000)]],
    supportCaseAttachments: [this.uploadedFiles],
    priority: [this.priorities[0]],
  });
  isOnDrag: boolean = false;
  isViewer: boolean = false;
  destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private userInfoService: UserInfoService,
    private chatWindowService: ChatWindowService,
    private chatReportService: ChatReportService
  ) {}

  ngOnInit(): void {
    this.preventWhiteSpace('subject');
    this.preventWhiteSpace('description');
    this.userInfoService.userSubject.pipe(takeUntil(this.destroy$)).subscribe({
      next: user => {
        if (user) {
          this.isViewer = user.role === AppConstant.ROLES.VIEWER.label;
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildPayload(): FormData {
    const payload = new FormData();
    payload.append(
      'subject',
      AppHelper.UtileFunctions.encodeBase64(
        this.formGroupReportIssue.value.subject
      )
    );
    payload.append(
      'description',
      AppHelper.UtileFunctions.encodeBase64(
        this.formGroupReportIssue.value.description
      )
    );
    if (this.formGroupReportIssue.value.supportCaseAttachments)
      this.formGroupReportIssue.value.supportCaseAttachments.forEach(
        (file: File) => {
          const fileName = AppHelper.UtileFunctions.encodeFileName(file.name);
          payload.append('supportCaseAttachments', file, fileName);
        }
      );
    payload.append(
      'priority',
      this.formGroupReportIssue.value.priority.priorityId
    );
    return payload;
  }

  public onCancel(): void {
    this.chatWindowService.setCurrentStep();
  }

  public onSend(): void {
    this.chatWindowService.setLoading(true);

    // Run both API calls in parallel, handling individual failures
    forkJoin({
      call_service_now: this.chatReportService
        .sendReportIssueToServiceNow(this.buildPayload())
        .pipe(catchError(error => of(null))), // Return null instead of error
      call_internal: this.chatReportService
        .sendReportIssue(this.buildPayload())
        .pipe(catchError(error => of(null))), // Return null instead of error
    })
      .pipe(
        finalize(() => this.chatWindowService.setLoading(false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: results => {
          // Check if at least one call succeeded
          if (results.call_service_now || results.call_internal) {
            this.notificationService.setMessage({
              type: AppConstant.MESSAGE_TYPE.SUCCESS,
              header: 'Information',
              content: 'Report issue was sent successfully.',
            });
            this.chatWindowService.setCurrentStep('SENT');
          } else {
            // Both calls failed
            this.notificationService.setMessage({
              type: AppConstant.MESSAGE_TYPE.ERROR,
              header: 'Information',
              content: 'Failed to send report. Please try again.',
            });
          }
        },
        error: error => {
          // This should rarely happen now since we catch errors individually
          console.log(error);
          this.notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.ERROR,
            header: 'Information',
            content: error,
          });
        },
      });
  }

  public onUploaderSelect(event: any): void {
    const filesList: Array<File> = [...event.files];
    const MAXIMIN_TOTAL_FILES_SIZE = 10485760; // 10MB = 10485760bytes
    const MAXIMUN_NUMBER_FILE_UPLOAD = 5;
    const listErr: IMessage[] = [];

    // Calculate total file size is uploaded
    let currentTotalFileSize =
      this.uploadedFiles?.reduce(
        (acc: number, file: File) => acc + file.size,
        0
      ) || 0;

    for (let i = 0; i < filesList.length; i++) {
      let file = filesList[i];

      // Verify duplicate
      const isDuplicated = this.uploadedFiles?.some((f: File) => {
        return f.name === file.name && f.size == file.size;
      });
      if (isDuplicated) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Duplicated file',
          content: 'Your uploading file is duplicated.',
        });
        // Remove file dublicate from list, decrease i, continue process.
        filesList.splice(i--, 1);
        continue;
      }

      // Verify file type
      const mimeTypeAllow = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/pdf',
      ];
      if (!mimeTypeAllow.includes(file.type.toLowerCase())) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: `Invalid File Type`,
          content:
            'Invalid file type. Allowed file types: .pdf,.jpg, .jpeg, .png',
        });
        // Remove Invalid file type from list, decrease i, continue process.
        filesList.splice(i--, 1);
        continue;
      }

      // Verify file name
      let formatSpecialChar = /[!()\[\]{}\\|<>\/?'"]+/;
      if (
        formatSpecialChar.test(
          AppHelper.StringFunctions.extractFileName(file.name)
        )
      ) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Detected: Invalid file name',
          content:
            "A file name can't contain any of the following characters: ! ' \" { } [ ] ( ) \\ / : ? < > | ",
        });
        filesList.splice(i--, 1);
        continue;
      }

      // Verify file size 0 byte
      if (file.size == 0) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid file size',
          content: 'Empty file cannot be uploaded.',
        });
        // Remove Invalid file size from list, decrease i, continue process.
        filesList.splice(i--, 1);
        continue;
      }

      // Verify file size 10000000
      if (file.size > MAXIMIN_TOTAL_FILES_SIZE!) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid file size',
          content: 'Allowed maximum file size: 10MB',
        });
        // Remove Invalid file size from list, decrease i, continue process.
        filesList.splice(i--, 1);
        continue;
      }

      if (currentTotalFileSize + file.size > MAXIMIN_TOTAL_FILES_SIZE) {
        listErr.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid total file size',
          content:
            'File make exceeded total allowed file size. Should be removed',
        });

        filesList.splice(i--, 1);
        continue;
      } else {
        currentTotalFileSize += file.size;
      }
    }

    // Verify file number
    if (
      this.uploadedFiles?.length + filesList?.length >
      MAXIMUN_NUMBER_FILE_UPLOAD
    ) {
      const numberFilesCanAdd =
        MAXIMUN_NUMBER_FILE_UPLOAD - this.uploadedFiles?.length;
      const numberFileRemoved = filesList.length - numberFilesCanAdd;

      listErr.push({
        type: AppConstant.MESSAGE_TYPE.WARNING,
        header: `Exceeded allowed number of files`,
        content: `Maximum ${MAXIMUN_NUMBER_FILE_UPLOAD} files are allowed`,
      });
      filesList.splice(numberFilesCanAdd, numberFileRemoved);
    }

    // Remove notification duplicate
    for (let i = 0; i < listErr.length; i++) {
      this.notificationService.setMessage(listErr[i]);
    }

    this.uploadedFiles = [...this.uploadedFiles, ...filesList];
    this.formGroupReportIssue
      .get('supportCaseAttachments')
      ?.setValue(this.uploadedFiles);
  }

  public onUploaderClear(uploader: string): void {
    this.uploadedFiles = [];
    this.formGroupReportIssue.get(uploader)?.setValue(null);
    this.formGroupReportIssue.get(uploader)?.markAsUntouched();
  }

  async onPasteClipBoardFile(): Promise<void> {
    try {
      const clipboardItems: ClipboardItem[] = await navigator.clipboard.read();
      const blobOutput: Blob = await clipboardItems[0]?.getType('image/png');
      const MAXIMUN_NUMBER_FILE_UPLOAD = 5;
      const MAXIMIN_TOTAL_FILES_SIZE = 10485760; // 10MB = 10485760bytes

      if (blobOutput) {
        const fileName = this.prepareFileName(this.uploadedFiles);

        const file: File = new File([blobOutput], fileName, {
          type: 'image/png',
        });
        if (file.size <= MAXIMIN_TOTAL_FILES_SIZE / 2) {
          if (this.uploadedFiles?.length >= MAXIMUN_NUMBER_FILE_UPLOAD) {
            this.notificationService.setMessage({
              type: AppConstant.MESSAGE_TYPE.WARNING,
              header: `Exceeded allowed number of files`,
              content: `Maximum ${MAXIMUN_NUMBER_FILE_UPLOAD} files are allowed`,
            });
          } else {
            this.notificationService.setMessage({
              type: AppConstant.MESSAGE_TYPE.SUCCESS,
              header: 'Clipboard',
              content: 'Pasted screenshot accepted.',
            });
            this.onUploaderSelect({ files: [file] });
          }
        } else {
          this.notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.WARNING,
            header: 'Clipboard',
            content: 'Pasted screenshot is larger than 5MB.',
          });
        }
      }
    } catch (e) {
      this.notificationService.setMessage({
        type: AppConstant.MESSAGE_TYPE.WARNING,
        header: 'Clipboard',
        content: 'Pasted screenshot is invalid.',
      });
    }
  }

  private prepareFileName(listFileExists: any[]): string {
    let number: number = 1;
    let nameString: string = '';
    let findName: boolean = false;
    while (!findName) {
      nameString = `pasted-screenshot-${number}.png`;
      const result = listFileExists.some((f: any) => f.name === nameString);
      if (result) {
        number++;
      } else {
        findName = !findName;
      }
    }

    return nameString;
  }

  public preventWhiteSpace(formControl: string): void {
    this.formGroupReportIssue
      .get(formControl)
      ?.valueChanges.subscribe(value => {
        if (value === ' ')
          this.formGroupReportIssue.get(formControl)?.patchValue('');
        this.formGroupReportIssue.get(formControl)?.markAsPristine();
        this.formGroupReportIssue.get(formControl)?.markAsUntouched();
      });
  }

  public openSelectFileUpload(): void {
    this.supportCaseAttachments.choose();
  }

  public removeItemUpload(index: number): void {
    this.supportCaseAttachments._files.splice(index, 1);
    this.uploadedFiles.splice(index, 1);
    this.formGroupReportIssue
      .get('supportCaseAttachments')
      ?.setValue(this.uploadedFiles);
  }

  public dragTime(event: any): void {
    this.isOnDrag = true;
    this.uploadedFiles = [];
    this.supportCaseAttachments._files.forEach(file =>
      this.uploadedFiles.push(file)
    );
    this.formGroupReportIssue
      .get('supportCaseAttachments')
      ?.setValue(this.uploadedFiles);
  }
}
